import {
  Avatar,
  Button,
  DatePipe,
  EnvironmentBanner,
  FileHashPipe,
} from '@abb-procure/common';
import { ENVIRONMENT_INFO } from '@abb-procure/constants';
import {
  DocumentsApiService,
  LoginUserService,
  LookupDataState,
  ResourceLinkSlimModel,
  UserState,
} from '@abb-procure/data';
import { Inbox } from '@abb-procure/inbox';
import { Search } from '@abb-procure/search';
import { SnackbarService } from '@abb-procure/snackbar';
import { BooleanInput } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { QuicklinkDirective } from 'ngx-quicklink';

@Component({
  // Existing usage is attribute-based in the shell layout; changing this would break templates.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[procure-app-header]',
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  imports: [
    EnvironmentBanner,
    RouterLink,
    Button,
    MatIcon,
    MatTooltip,
    Search,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    Avatar,
    Inbox,
    QuicklinkDirective,
    DatePipe,
    FileHashPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHeader {
  readonly isEnvironmentBannerEnabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  protected readonly isExpanded = signal(false);
  protected readonly environmentInfo = inject(ENVIRONMENT_INFO);
  protected readonly currentUser = computed(() => this.userState.current());

  protected readonly isSubstitute = computed(() =>
    this.userState.isSubstitute(),
  );

  protected readonly menu = computed(() => this.lookupDataState.menu());
  protected readonly tsp = computed(() => this.lookupDataState.tsp());

  private readonly userState = inject(UserState);
  private readonly lookupDataState = inject(LookupDataState);
  private readonly loginUserService = inject(LoginUserService);
  private readonly documentsApiService = inject(DocumentsApiService);
  private currentDownloads: ResourceLinkSlimModel[] = [];
  private readonly snackbarService = inject(SnackbarService);

  protected resourceClick(resource: ResourceLinkSlimModel | undefined): void {
    if (!resource?.isDocument) {
      return;
    }

    this.downloadSingleDocument(resource);
  }

  protected logoutSubstituteUser(): void {
    this.loginUserService.stopImpersonatingUser();

    this.snackbarService.showInfoSnackbar({
      text: 'Returned to login user.',
    });

    window.location.reload();
  }

  protected logout(): void {
    this.loginUserService.logout$();
  }

  protected toggleMenu(): void {
    this.isExpanded.update((state) => !state);
  }

  private async downloadSingleDocument(
    resource: ResourceLinkSlimModel,
  ): Promise<void> {
    if (this.isCurrentDownloading(resource)) {
      return;
    }

    this.currentDownloads = [...this.currentDownloads, resource];

    const response = await this.documentsApiService.getResourceDownloadUrl(
      resource.id,
    );

    this.currentDownloads = this.currentDownloads.filter(
      (el) => el.id !== resource.id,
    );

    if (response) {
      window.location.href = response;
    }
  }

  private isCurrentDownloading(resource?: ResourceLinkSlimModel): boolean {
    return (
      this.currentDownloads.find((el) => resource?.id === el.id) !== undefined
    );
  }
}
